// ============================================================
//  Sidebar.tsx — SAP-style Professional Navigation
// ============================================================
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "./MainLayout";
import usePermission from "../usePermission";
import apiClient from "../utils/apiClient";
import API_URL from "../config";
import { useBranding } from "../utils/BrandingContext";
import { getSelectedCompanyId } from "../utils/companyContext";

// ── Inline SVG Icons ─────────────────────────────────────────
const IC: Record<string, React.ReactElement> = {
  dashboard:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  hr:         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>,
  qc:         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
  tax:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  asset:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>,
  master:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  pricing:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  inventory:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
  purchase:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>,
  sales:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
  production: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M19.07 19.07l-1.41-1.41M4.93 19.07l1.41-1.41M12 2v2M12 20v2M2 12h2M20 12h2"/></svg>,
  finance:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6"/></svg>,
  approval:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  users:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  audit:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  settings:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M19.07 19.07l-1.41-1.41M4.93 19.07l1.41-1.41M12 2v2M12 20v2M2 12h2M20 12h2"/></svg>,
  chevronR:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  chevronD:   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  alerts:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  pm:         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  oee:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 5-5"/></svg>,
  serial:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="10" rx="2"/><line x1="22" y1="11" x2="22" y2="13"/><line x1="6" y1="10" x2="6" y2="14"/><line x1="10" y1="10" x2="10" y2="14"/><line x1="14" y1="10" x2="14" y2="14"/><line x1="18" y1="10" x2="18" y2="14"/></svg>,
  hazmat:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  service:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11H1l8-8 8 8h-8v10"/><path d="M22 21a3 3 0 0 0-3-3h-6a3 3 0 0 0-3 3"/><circle cx="16" cy="11" r="3"/></svg>,
  sfc:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  cost:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6"/><path d="M3 9h18M3 15h18"/></svg>,
  dms:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/><line x1="9" y1="11" x2="15" y2="11"/></svg>,
  workflow:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  bi:         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  mc:         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/></svg>,
  portal:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
};

// ── Module accent colors ──────────────────────────────────────
const COLORS: Record<string, string> = {
  "Master Data":     "#6366F1",
  "Pricing":         "#F59E0B",
  "Inventory":       "#0891B2",
  "Purchase":        "#D97706",
  "Sales":           "#059669",
  "Production":      "#7C3AED",
  "Quality Control": "#D97706",   // amber — quality orange
  "Finance":         "#DC2626",
  "HR":              "#0891B2",   // teal — human resources
  "Plant Maintenance": "#EA580C", // orange — maintenance
  "OEE":             "#059669", // emerald — OEE efficiency
  "Serial Lifecycle": "#DC2626", // red — battery safety/compliance
  "Hazmat & Compliance": "#B45309", // amber — chemical hazard
  "Service / AMC":       "#0891B2", // cyan — after sales service
  "Shop Floor":          "#DB2777", // pink — shop floor
  "Cost Accounting":     "#0891B2", // teal — costing
  "Documents":           "#6366F1", // indigo — DMS
  "Workflow":            "#F59E0B", // amber — automation
  "BI / Analytics":      "#0EA5E9", // sky — BI
  "Multi-Company":       "#4338CA", // indigo — consolidation
  "Customer Portal":     "#0D9488", // teal — customer portal
  "Pricing Setup":       "#F59E0B", // amber — pricing engine
  "Tools":               "#7C3AED", // purple — tools & utilities
  // New consolidated groups
  "Manufacturing":       "#DB2777", // pink — manufacturing ops umbrella
  "Analytics & Workflow":"#0EA5E9", // sky — analytics umbrella
  "Admin":               "#64748B", // slate — admin & settings
};

// ── Navigation Structure ──────────────────────────────────────
// Consolidated 13 top-level groups (down from 32) — all routes preserved.
type NavLeaf = { label: string; path: string; mod: string; icon?: string };
type NavItem = { label: string; icon?: string; path?: string; mod?: string; children?: NavLeaf[] };
const EMPTY_NAV: NavItem[] = [];

const NAV: NavItem[] = [
  { label: "Dashboard",        path: "/dashboard",       icon: "dashboard", mod: "dashboard" },
  { label: "Smart Alerts",     path: "/alerts",          icon: "alerts",    mod: "dashboard" },

  {
    label: "Master Data", icon: "master",
    children: [
      { label: "Business Partners", path: "/master/bp", mod: "business_partners" },
    ],
  },

  // ── Pricing — its own group (moved out of Master Data 16 May 2026) ──
  {
    label: "Pricing", icon: "pricing",
    children: [
      { label: "Pricing Dashboard",  path: "/pricing/dashboard",       mod: "business_partners" },
      { label: "Pricing Categories", path: "/pricing/setup",           mod: "business_partners" },
      { label: "Commodity Rates",    path: "/pricing/commodity",       mod: "business_partners" },
      { label: "Cost Sheet",         path: "/pricing/cost-sheet",      mod: "business_partners" },
      { label: "Rate Sheet",         path: "/pricing/rate-sheet",      mod: "business_partners" },
      { label: "Customer Overrides", path: "/pricing/overrides",       mod: "business_partners" },
      { label: "Production Cost",   path: "/pricing/production-cost", mod: "business_partners" },
      { label: "Bulk Operations",    path: "/pricing/bulk",            mod: "business_partners" },
      { label: "Currency Rates",     path: "/pricing/currency",        mod: "business_partners" },
      { label: "Sales Performance",  path: "/pricing/sales-report",    mod: "business_partners" },
      { label: "Below-Cost Log",     path: "/pricing/below-cost-log",  mod: "business_partners" },
    ],
  },

  {
    label: "Inventory", icon: "inventory",
    children: [
      { label: "Item Master",        path: "/inventory/items",          mod: "item_master"  },
      { label: "Warehouses",         path: "/inventory/warehouse",      mod: "warehouse"    },
      { label: "Stock In (GRN)",     path: "/inventory/grn",            mod: "stock_in"     },
      { label: "Stock Ledger",       path: "/inventory/ledger",         mod: "stock_ledger" },
      { label: "Good Issue / GR",    path: "/inventory/gi",             mod: "stock_ledger" },
      { label: "Stock Valuation",    path: "/inventory/stock-valuation",mod: "stock_ledger" },
      { label: "Stock Transfer",     path: "/inventory/stock-transfer", mod: "stock_ledger" },
      { label: "Warehouse (WMS)",    path: "/inventory/wms",            mod: "stock_ledger" },
      { label: "Physical Count",     path: "/inventory/physical-count", mod: "stock_ledger" },
      { label: "Stock & Finance Recon", path: "/inventory/stock-finance-reconciliation", mod: "stock_ledger" },
    ],
  },

  {
    label: "Purchase", icon: "purchase",
    children: [
      { label: "Requisition",        path: "/purchase/requisition",     mod: "purchase_requisition" },
      { label: "Purchase Orders",    path: "/purchase/orders",          mod: "purchase_order" },
      { label: "GRN",                path: "/purchase/grn",             mod: "grn"            },
      { label: "Purchase Return",    path: "/purchase/return",          mod: "purchase_return" },
      { label: "AP Invoice",         path: "/purchase/ap-invoice",      mod: "ap_invoice"     },
      { label: "Vendor Payment",     path: "/purchase/vendor-payment",  mod: "vendor_payment" },
      { label: "3-Way Matching",     path: "/purchase/three-way-match", mod: "three_way_match" },
      { label: "GSTR-2B Recon",      path: "/purchase/gstr2b",          mod: "gstr2b"          },
      { label: "RFQ",                path: "/purchase/rfq",             mod: "rfq"             },
      { label: "Reports",            path: "/purchase/reports",         mod: "purchase_reports" },
    ],
  },

  {
    label: "Sales", icon: "sales",
    children: [
      { label: "Quotations",         path: "/sales/quotations",         mod: "sales_quotation" },
      { label: "Sales Orders",       path: "/sales/orders",             mod: "sales_order"      },
      { label: "Delivery",           path: "/sales/delivery",           mod: "delivery"         },
      { label: "AR Invoice",         path: "/sales/ar-invoice",         mod: "ar_invoice"       },
      { label: "Customer Payment",   path: "/sales/customer-payment",   mod: "customer_payment" },
      { label: "Sales Return",       path: "/sales/returns",            mod: "sales_return"     },
      { label: "Battery Buyback",    path: "/sales/battery-buyback",    mod: "battery_buyback"  },
      { label: "Sale Tracker",       path: "/sales/sale-tracker",       mod: "sale_tracker"     },
      { label: "Warranty Claims",    path: "/sales/warranty-claims",    mod: "warranty_claims"  },
      { label: "Battery Timeline",   path: "/serial/tracker",           mod: "sales_order"      },
      { label: "Serial Register",    path: "/serial/register",          mod: "serial_register"  },
      { label: "Lifecycle Dashboard",path: "/serial/dashboard",         mod: "serial_dashboard" },
      { label: "Recycling (CPCB)",   path: "/serial/recycling",         mod: "recycling_batches" },
      { label: "Dispatch Scan",      path: "/sales/dispatch-scan",      mod: "dispatch_scan"    },
      { label: "E-Invoice (IRN)",    path: "/sales/einvoice",           mod: "einvoice"         },
      { label: "E-Way Bill",         path: "/sales/eway-bill",          mod: "eway_bill"        },
      { label: "Credit Management",  path: "/sales/credit-management",  mod: "credit_management" },
      { label: "Payment Follow-up",  path: "/sales/payment-followup",   mod: "payment_followup" },
      { label: "Sales Targets",      path: "/sales/targets",            mod: "sales_targets"    },
    ],
  },

  {
    label: "Production", icon: "production",
    children: [
      { label: "Production Overview",    path: "/production/orders",          mod: "production_order" },
      { label: "Grid Plate Production",  path: "/production/grid-plate",      mod: "grid_plate_production" },
      { label: "Spine Plate Production", path: "/production/spine-plate",     mod: "spine_plate_production" },
      { label: "Battery Assembly",       path: "/production/battery-assembly",mod: "battery_assembly" },
      { label: "WIP Tracking",       path: "/production/wip",     mod: "production" },
      { label: "Bill of Materials",  path: "/production/bom",     mod: "bom"              },
      { label: "Barcode & Labels",   path: "/production/barcode", mod: "barcode_labels" },
      { label: "MRP Planning",       path: "/production/mrp",          mod: "mrp_planning" },
      { label: "MRP Workbench",      path: "/production/mrp-workbench", mod: "mrp_workbench" },
      { label: "Plate Stock",        path: "/production/plate-stock",    mod: "plate_stock" },
      { label: "Battery Stock",      path: "/production/battery-stock",  mod: "battery_stock" },
    ],
  },

  {
    // Manufacturing umbrella — QC + Plant Maint + OEE + Hazmat + SFC merged
    label: "Manufacturing", icon: "qc",
    children: [
      // ── Quality Control ──
      { label: "QC Dashboard",   path: "/qc/dashboard",   mod: "production_order" },
      { label: "QC Templates",   path: "/qc/templates",   mod: "production_order" },
      { label: "QC Inspections", path: "/qc/inspections", mod: "production_order" },
      { label: "NCR Management", path: "/qc/ncr",         mod: "production_order" },
      // ── Plant Maintenance ──
      { label: "PM Dashboard",   path: "/pm/dashboard",    mod: "production_order" },
      { label: "Machine Master", path: "/pm/machines",     mod: "production_order" },
      { label: "Work Orders",    path: "/pm/work-orders",  mod: "production_order" },
      { label: "Breakdown Log",  path: "/pm/breakdowns",   mod: "production_order" },
      // ── OEE ──
      { label: "OEE Dashboard",     path: "/oee/dashboard",      mod: "production_order" },
      { label: "OEE Production Log",path: "/oee/production-log", mod: "production_order" },
      { label: "OEE Masters",       path: "/oee/masters",        mod: "production_order" },
      // ── Hazmat & Compliance ──
      { label: "Hazmat Dashboard",  path: "/hazmat/dashboard",  mod: "production_order" },
      { label: "Hazmat Materials",  path: "/hazmat/materials",  mod: "production_order" },
      { label: "Waste Manifests",   path: "/hazmat/waste",      mod: "production_order" },
      { label: "Emissions/Incidents", path: "/hazmat/emissions", mod: "production_order" },
      { label: "Licenses · EPR",    path: "/hazmat/licenses",   mod: "production_order" },
      // ── Shop Floor Terminal ──
      { label: "SFC Dashboard",     path: "/sfc/dashboard", mod: "production_order" },
      { label: "SFC Admin",         path: "/sfc-admin",     mod: "production_order" },
    ],
  },

  {
    // Finance umbrella — Finance + Cost Accounting merged
    label: "Finance", icon: "finance",
    children: [
      { label: "Chart of Accounts",   path: "/finance/coa",              mod: "chart_of_accounts" },
      { label: "Opening Balance",     path: "/finance/opening-balance",  mod: "chart_of_accounts" },
      { label: "Journal Entry",       path: "/finance/journal",          mod: "journal_entry"     },
      { label: "Reports",             path: "/finance/reports",          mod: "reports"           },
      { label: "Budget Control",      path: "/finance/budget",           mod: "budget"            },
      { label: "GL Determination",    path: "/finance/gl-determination", mod: "chart_of_accounts" },
      { label: "Bank Reconciliation", path: "/finance/bank-recon",       mod: "journal_entry"     },
      { label: "Recurring Postings",  path: "/finance/recurring-je",     mod: "journal_entry"     },
      { label: "Period-End Closing",  path: "/finance/period-close",     mod: "journal_entry"     },
      { label: "Period Management",   path: "/finance/periods",          mod: "journal_entry"     },
      { label: "Statement of Account", path: "/finance/statement-of-account", mod: "reports"       },
      { label: "Cash Flow Forecast",  path: "/finance/cash-flow",        mod: "reports"           },
      { label: "Tax Master (GST)",    path: "/finance/tax-master",       mod: "chart_of_accounts" },
      { label: "TDS Reports",         path: "/finance/tds-report",       mod: "chart_of_accounts" },
      { label: "Fixed Assets",        path: "/finance/fixed-assets",     mod: "chart_of_accounts", icon: "asset" },
      { label: "MIS Reports",         path: "/mis/reports",              mod: "reports"           },
      // ── Cost Accounting (merged in) ──
      { label: "Cost Dashboard",      path: "/cost/dashboard", mod: "reports" },
      { label: "Cost Masters",        path: "/cost/masters",   mod: "reports" },
      { label: "Standard Cost",       path: "/cost/standards", mod: "reports" },
      { label: "Actual & Variance",   path: "/cost/actuals",   mod: "reports" },
    ],
  },

  {
    label: "HR", icon: "hr",
    children: [
      { label: "HR Dashboard",     path: "/hr",                 mod: "hr" },
      { label: "Departments",      path: "/hr/departments",     mod: "hr" },
      { label: "Designations",     path: "/hr/designations",    mod: "hr" },
      { label: "Employees",        path: "/hr/employees",       mod: "hr" },
      { label: "Attendance",       path: "/hr/attendance",      mod: "hr" },
      { label: "Leave Management", path: "/hr/leaves",          mod: "hr" },
      { label: "Admin Payroll",     path: "/hr/payroll-admin",   mod: "payroll_foundation" },
      { label: "Sales Payroll",     path: "/hr/payroll-sales",   mod: "payroll_foundation" },
      { label: "Labour Payroll",    path: "/hr/payroll-labour",  mod: "payroll_foundation" },
      { label: "Hiring",           path: "/hr/hiring",          mod: "hr" },
      { label: "Expense Claims",   path: "/hr/expenses",        mod: "hr" },
    ],
  },

  {
    // Sales Channel — CRM + Dealer + Customer Portal + Service/AMC
    label: "Sales Channel", icon: "sales", mod: "sales_order",
    children: [
      { label: "CRM",               path: "/crm",               mod: "sales_order" },
      { label: "Dealer Management", path: "/dealer",            mod: "sales_order" },
      { label: "Dealer Performance",path: "/dealer/performance",mod: "sales_order" },
      { label: "Customer Portal",   path: "/portal-admin",      mod: "sales_order" },
      // ── Service / AMC (merged in) ──
      { label: "Service Dashboard", path: "/service/dashboard",   mod: "sales_order" },
      { label: "Service Tickets",   path: "/service/tickets",     mod: "sales_order" },
      { label: "Technicians",       path: "/service/technicians", mod: "sales_order" },
      { label: "AMC Contracts",     path: "/service/amc",         mod: "sales_order" },
    ],
  },

  {
    // Analytics & Workflow — BI + Multi-Company + Workflow + Documents
    label: "Analytics & Workflow", icon: "bi",
    children: [
      // ── BI / Analytics ──
      { label: "BI Dashboard",       path: "/bi/dashboard", mod: "reports" },
      { label: "Report Builder",     path: "/bi/builder",   mod: "reports" },
      { label: "Saved Reports",      path: "/bi/reports",   mod: "reports" },
      { label: "Materialized Views", path: "/admin/mviews", mod: "reports" },
      // ── Multi-Company ──
      { label: "MC Dashboard",       path: "/mc/dashboard",      mod: "reports" },
      { label: "Companies",          path: "/mc/companies",      mod: "reports" },
      { label: "IC Transactions",    path: "/mc/ic-txns",        mod: "reports" },
      { label: "Consolidated P&L",   path: "/mc/pnl",            mod: "reports" },
      { label: "Exchange Rates",     path: "/mc/exchange-rates", mod: "reports" },
      // ── Workflow ──
      { label: "Workflow Dashboard", path: "/workflow/dashboard", mod: "reports" },
      { label: "Workflow Builder",   path: "/workflow/builder",   mod: "reports" },
      { label: "Execution Log",      path: "/workflow/log",       mod: "reports" },
      // ── Documents (DMS) ──
      { label: "DMS Dashboard",      path: "/dms/dashboard",  mod: "reports" },
      { label: "Document Explorer",  path: "/dms",            mod: "reports" },
      { label: "DMS Approvals",      path: "/dms/approvals",  mod: "reports" },
    ],
  },

  {
    // Admin — Approvals + Users + Audit + Notifications + Settings + Tools merged
    label: "Admin", icon: "settings",
    children: [
      { label: "Approval Inbox",  path: "/approvals/inbox",  mod: "approval"         },
      { label: "Users",           path: "/users",            mod: "users"            },
      { label: "Super Admin Control", path: "/admin/super-control", mod: "platform_control" },
      { label: "Website Leads",   path: "/admin/website-leads", mod: "users"        },
      { label: "Security Dashboard", path: "/audit",         mod: "audit_log"        },
      { label: "Production Monitor", path: "/admin/monitor", mod: "audit_log"        },
      { label: "Notifications",   path: "/notifications",    mod: "company_settings" },
      // ── Settings ──
      // NOTE: Print Designer now LIVES INSIDE Company Settings → "Print Designer" tab.
      // Centralized control: change once in Company Settings → applies to all PDFs across ERP.
      { label: "Company Settings",path: "/settings/company", mod: "company_settings" },
      { label: "Domain Control",  path: "/admin/domain-control", mod: "domain_control" },
      { label: "Brand Settings",  path: "/settings/branding",mod: "company_settings" },
      { label: "Custom Fields",   path: "/admin/custom-fields", mod: "company_settings" },
      // ── Tools ──
      { label: "Tally Importer",     path: "/tools/tally",      mod: "users" },
      { label: "Project Mgmt",       path: "/projects",         mod: "users" },
      { label: "API Tokens",         path: "/admin/api-tokens", mod: "users" },
      { label: "Dropdown Master",    path: "/admin/dropdowns",  mod: "platform_control" },
    ],
  },
];

type CatalogNavRow = {
  module_code?: unknown;
  module_name?: unknown;
  module_group?: unknown;
  route_path?: unknown;
  permission_module?: unknown;
  icon_key?: unknown;
  display_order?: unknown;
  is_enabled?: unknown;
};

const GROUP_ICON_KEYS: Record<string, string> = {
  "Master Data": "master",
  Pricing: "pricing",
  Inventory: "inventory",
  Purchase: "purchase",
  Sales: "sales",
  Production: "production",
  Manufacturing: "qc",
  Finance: "finance",
  HR: "hr",
  "Sales Channel": "sales",
  "Analytics & Workflow": "bi",
  Admin: "settings",
};

const catalogToNav = (value: unknown): NavItem[] | null => {
  if (!Array.isArray(value)) return null;
  const rows = value
    .filter((row): row is CatalogNavRow => !!row && typeof row === "object")
    .filter(row => row.is_enabled !== false)
    .map((row, index) => ({
      label: typeof row.module_name === "string" ? row.module_name.trim() : "",
      path: typeof row.route_path === "string" ? row.route_path.trim() : "",
      mod: typeof row.permission_module === "string" && row.permission_module.trim()
        ? row.permission_module.trim()
        : String(row.module_code || "").trim(),
      group: typeof row.module_group === "string" && row.module_group.trim()
        ? row.module_group.trim()
        : "General",
      icon: typeof row.icon_key === "string" ? row.icon_key.trim() : "",
      order: Number.isFinite(Number(row.display_order)) ? Number(row.display_order) : index,
    }))
    .filter(row => !!row.label && !!row.path && !!row.mod)
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));

  const items: NavItem[] = [];
  const groupPositions = new Map<string, number>();
  rows.forEach(row => {
    if (row.group.toLowerCase() === "core") {
      items.push({
        label: row.label,
        path: row.path,
        mod: row.mod,
        icon: row.icon || (row.path === "/alerts" ? "alerts" : "dashboard"),
      });
      return;
    }
    let position = groupPositions.get(row.group);
    if (position === undefined) {
      position = items.length;
      groupPositions.set(row.group, position);
      items.push({
        label: row.group,
        icon: row.icon || GROUP_ICON_KEYS[row.group] || "settings",
        children: [],
      });
    }
    const group = items[position];
    if (group.children) {
      group.children.push({ label: row.label, path: row.path, mod: row.mod });
    }
  });
  return items;
};

// ── Sidebar ───────────────────────────────────────────────────
const moduleCacheKey = (companyId: number) => `erp_enabled_modules_${companyId || "default"}`;
const enabledRoutesCacheKey = (companyId: number) => `erp_enabled_routes_${companyId || "default"}`;
const disabledRoutesCacheKey = (companyId: number) => `erp_disabled_routes_${companyId || "default"}`;
const navigationCacheKey = (companyId: number) => `erp_navigation_${companyId || "default"}`;

const readStoredSet = (key: string): Set<string> | null => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "null");
    return Array.isArray(parsed)
      ? new Set(parsed.filter((item: unknown) => typeof item === "string"))
      : null;
  } catch {
    return null;
  }
};

const readStoredNavigation = (companyId: number): NavItem[] | null => {
  try {
    return catalogToNav(JSON.parse(localStorage.getItem(navigationCacheKey(companyId)) || "null"));
  } catch {
    return null;
  }
};

const cleanRoute = (path: string) => (path || "/").split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";

const Sidebar = () => {
  const location                    = useLocation();
  const { collapsed, setCollapsed } = useSidebar();
  const { can }                     = usePermission();
  const { branding }                = useBranding();

  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    NAV.forEach(item => {
      if (item.children) {
        init[item.label] = item.children.some(c => location.pathname.startsWith(c.path.split("?")[0]));
      }
    });
    return init;
  });

  const toggle   = (label: string) => setOpen(p => ({ ...p, [label]: !p[label] }));
  const isActive = (path: string)  => location.pathname === path.split("?")[0];
  const currentCompanyId = getSelectedCompanyId();
  const [enabledModules, setEnabledModules] = useState<Set<string> | null>(() => (
    readStoredSet(moduleCacheKey(currentCompanyId))
  ));
  const [disabledRoutes, setDisabledRoutes] = useState<Set<string> | null>(() => (
    readStoredSet(disabledRoutesCacheKey(currentCompanyId))
  ));
  const [catalogNav, setCatalogNav] = useState<NavItem[] | null>(() => (
    readStoredNavigation(currentCompanyId)
  ));
  // Runtime navigation is database-catalog only. Without a company-scoped
  // response/cache, fail closed instead of exposing a hardcoded full menu.
  const effectiveNav = catalogNav ?? EMPTY_NAV;
  const moduleEnabled = (mod: string) => Boolean(enabledModules?.has(mod));
  const routeEnabled = (path?: string) => {
    if (!path || !disabledRoutes) return true;
    const clean = cleanRoute(path);
    return !Array.from(disabledRoutes).some((prefix) => {
      const route = cleanRoute(prefix);
      return clean === route || clean.startsWith(`${route}/`);
    });
  };
  const canView = (mod: string, path?: string) => {
    return routeEnabled(path) && moduleEnabled(mod) && can(mod, "view");
  };

  useEffect(() => {
    let cancelled = false;
    const applyCachedAccess = () => {
      const companyId = getSelectedCompanyId();
      setEnabledModules(readStoredSet(moduleCacheKey(companyId)));
      setDisabledRoutes(readStoredSet(disabledRoutesCacheKey(companyId)));
      setCatalogNav(readStoredNavigation(companyId));
    };
    const loadModuleAccess = async () => {
      const companyId = getSelectedCompanyId();
      try {
        const r = await apiClient.get(`${API_URL}/api/platform-control/me/modules`, {
          params: companyId > 0 ? { company_id: companyId } : undefined,
          headers: companyId > 0 ? { "X-Company-ID": String(companyId) } : undefined,
        });
        const responseCompanyId = Number(r.data?.company_id || companyId || 0);
        const enabled = Array.isArray(r.data?.enabled_modules) ? r.data.enabled_modules : null;
        const enabledRoutes = Array.isArray(r.data?.enabled_route_paths) ? r.data.enabled_route_paths : null;
        const disabledRoutesData = Array.isArray(r.data?.disabled_route_paths) ? r.data.disabled_route_paths : null;
        const navigationData = Array.isArray(r.data?.navigation) ? r.data.navigation : null;
        if (!cancelled && enabled) {
          setEnabledModules(new Set(enabled));
          try {
            localStorage.setItem(moduleCacheKey(responseCompanyId), JSON.stringify(enabled));
          } catch {}
        }
        if (!cancelled && enabledRoutes) {
          try {
            localStorage.setItem(enabledRoutesCacheKey(responseCompanyId), JSON.stringify(enabledRoutes));
          } catch {}
        }
        if (!cancelled && disabledRoutesData) {
          setDisabledRoutes(new Set(disabledRoutesData));
          try {
            localStorage.setItem(disabledRoutesCacheKey(responseCompanyId), JSON.stringify(disabledRoutesData));
          } catch {}
        }
        if (!cancelled && navigationData) {
          const nextNavigation = catalogToNav(navigationData);
          setCatalogNav(nextNavigation);
          try {
            localStorage.setItem(navigationCacheKey(responseCompanyId), JSON.stringify(navigationData));
          } catch {}
        }
      } catch {
        if (!cancelled) applyCachedAccess();
      }
    };
    loadModuleAccess();
    const refreshModuleAccess = () => {
      applyCachedAccess();
      loadModuleAccess();
    };
    window.addEventListener("aviraapro:company-context-changed", refreshModuleAccess);
    window.addEventListener("storage", refreshModuleAccess);
    window.addEventListener("focus", refreshModuleAccess);
    const t = setInterval(loadModuleAccess, 120_000);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("aviraapro:company-context-changed", refreshModuleAccess);
      window.removeEventListener("storage", refreshModuleAccess);
      window.removeEventListener("focus", refreshModuleAccess);
    };
  }, []);

  // Tooltip for collapsed icons
  const [tooltip, setTooltip] = useState<{ label: string; y: number } | null>(null);

  // ── SAP-style hide/show groups (Customize Sidebar) ───────
  // Hidden groups stored in localStorage per user. Click ⚙️ to manage.
  const HIDDEN_KEY = "erp_sidebar_hidden_groups";
  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(HIDDEN_KEY) || "[]")); }
    catch { return new Set(); }
  });
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const toggleGroupHidden = (label: string) => {
    setHiddenGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };

  const visibleNavItems = effectiveNav.reduce<NavItem[]>((items, item) => {
    if (item.path) {
      if (canView(item.mod || "", item.path)) items.push(item);
      return items;
    }

    const visibleChildren = item.children?.filter(child => canView(child.mod, child.path)) ?? [];
    if (visibleChildren.length > 0) {
      items.push({ ...item, children: visibleChildren } as NavItem);
    }
    return items;
  }, []);
  const visibleHiddenCount = visibleNavItems.filter(item => hiddenGroups.has(item.label)).length;

  // ── Expand-all / Collapse-all (single toggle button) ────
  // Considers only groups that are currently VISIBLE (not in hiddenGroups).
  const expandableGroups = visibleNavItems.filter(i => i.children && !hiddenGroups.has(i.label));
  const anyCollapsed = expandableGroups.some(i => !open[i.label]);
  const toggleAllGroups = () => {
    const target = anyCollapsed;  // if any collapsed → expand all; otherwise collapse all
    setOpen(prev => {
      const next = { ...prev };
      expandableGroups.forEach(i => { next[i.label] = target; });
      return next;
    });
  };

  // ── Menu search (SAP "Look Up Menus" style) ───────────────
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const matchesSearch = (item: any) => {
    if (!q) return true;
    if (item.label?.toLowerCase().includes(q)) return true;
    if (item.children?.some((c: any) => c.label?.toLowerCase().includes(q))) return true;
    return false;
  };

  // Auto-expand groups when searching (so matching children show)
  useEffect(() => {
    if (!q) return;
    setOpen(prev => {
      const next = { ...prev };
      effectiveNav.forEach(item => {
        if (item.children?.some((c: any) => c.label?.toLowerCase().includes(q))) {
          next[item.label] = true;
        }
      });
      return next;
    });
  }, [q, effectiveNav]);

  // ── Alert badge count (refresh every 60s) ─────────────────
  const [alertCounts, setAlertCounts] = useState<{ total: number; critical: number }>({ total: 0, critical: 0 });
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await apiClient.get(`${API_URL}/api/alerts/summary`);
        if (!cancelled) {
          setAlertCounts({
            total:    r.data?.total    || 0,
            critical: r.data?.critical || 0,
          });
        }
      } catch { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="erp-sidebar" style={{
      width: "100%", height: "100%",
      background: "linear-gradient(180deg, #0B1220 0%, #111827 40%, #0D1422 100%)",
      borderRight: "1px solid rgba(148,163,184,0.16)",
      display: "flex", flexDirection: "column",
      overflow: "hidden",
      position: "relative",
    }}>
      {/* Decorative top glow */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 60,
        background: "linear-gradient(180deg, rgba(29,78,216,0.08), transparent)",
        pointerEvents: "none", zIndex: 0,
      }} />

      {/* Animations + custom scrollbar */}
      <style>{`
        @keyframes pulseBadge {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.65); }
          50%      { box-shadow: 0 0 0 6px rgba(220,38,38,0); }
        }
        @keyframes navItemSlideIn {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.6; }
          50%      { opacity: 1; }
        }
        .erp-sidebar-scroll::-webkit-scrollbar { width: 5px; }
        .erp-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .erp-sidebar-scroll::-webkit-scrollbar-thumb {
          background: var(--erp-primary);
          border-radius: 3px;
        }
        .erp-sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: var(--erp-secondary);
        }
        .erp-nav-item {
          animation: navItemSlideIn 0.25s cubic-bezier(.34,1.56,.64,1);
        }
        .erp-nav-active-glow {
          animation: glowPulse 2.4s ease-in-out infinite;
        }
      `}</style>

      {/* ── SAP-style top header: Hamburger + Search ─────── */}
      <div style={{
        display: "flex", alignItems: "center",
        height: 44, flexShrink: 0,
        borderBottom: "1px solid rgba(99,102,241,0.15)",
        background: "linear-gradient(180deg, rgba(11,18,32,0.95), rgba(15,23,42,0.85))",
        backdropFilter: "blur(8px)",
        padding: collapsed ? 0 : "0 6px 0 0",
        gap: 4, zIndex: 2,
      }}>
        {/* Hamburger toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "Expand Menu" : "Collapse Menu"}
          style={{
            width: collapsed ? 56 : 40, minWidth: collapsed ? 56 : 40, height: "100%",
            background: "transparent", border: "none", cursor: "pointer",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
            flexShrink: 0,
          }}
          onMouseEnter={e => (e.currentTarget.style.background = "#1F2937")}
          onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
        >
          <span style={{ display: "block", width: 17, height: 1.5, background: "#94A3B8", borderRadius: 2 }} />
          <span style={{ display: "block", width: collapsed ? 11 : 17, height: 1.5, background: "#94A3B8", borderRadius: 2, transition: "width 0.2s" }} />
          <span style={{ display: "block", width: 17, height: 1.5, background: "#94A3B8", borderRadius: 2 }} />
        </button>

        {/* Search bar — only visible when expanded */}
        {!collapsed && (
          <div style={{
            flex: "1 1 0", minWidth: 0,   // ← minWidth:0 lets search shrink so ⊞ + ⚙ buttons stay visible
            display: "flex", alignItems: "center",
            background: "#1F2937", borderRadius: 6,
            padding: "0 10px", height: 30,
            border: "1px solid #374151",
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Look Up Menus"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1, border: "none", background: "transparent",
                color: "#E5E7EB", fontSize: 12, outline: "none",
                marginLeft: 6, minWidth: 0,
              }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                title="Clear"
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  color: "#9CA3AF", fontSize: 14, lineHeight: 1, padding: 0,
                  flexShrink: 0,
                }}
              >✕</button>
            )}
          </div>
        )}
        {/* Expand-all / Collapse-all toggle */}
        {!collapsed && (
          <button
            onClick={toggleAllGroups}
            title={anyCollapsed ? "Expand all groups" : "Collapse all groups"}
            style={{
              background: "transparent", border: "1px solid #374151",
              color: "#9CA3AF", cursor: "pointer", borderRadius: 5,
              width: 26, height: 26, fontSize: 13, padding: 0, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >{anyCollapsed ? "⊞" : "⊟"}</button>
        )}
        {/* ⚙️ Customize Sidebar (hide/show groups, SAP B1 style) */}
        {!collapsed && (
          <button
            onClick={() => setCustomizeOpen(true)}
            title="Customize sidebar — hide/show menu groups"
            style={{
              background: "transparent", border: "1px solid #374151",
              color: "#9CA3AF", cursor: "pointer", borderRadius: 5,
              width: 26, height: 26, fontSize: 13, padding: 0, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >⚙</button>
        )}
      </div>

      {/* ── Customize Sidebar modal ─────────────────────────── */}
      {customizeOpen && (
        <div
          onClick={() => setCustomizeOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
            zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 10, width: 440, maxWidth: "92vw", maxHeight: "80vh",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)", overflow: "hidden",
              display: "flex", flexDirection: "column",
            }}
          >
            <div style={{
              padding: "12px 16px", borderBottom: "1px solid #E2E8F0", background: "#F8FAFC",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>⚙ Customize Sidebar</div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                  Uncheck a group to hide it from your sidebar. Saved per browser.
                </div>
              </div>
              <button
                onClick={() => setCustomizeOpen(false)}
                style={{ border: "none", background: "transparent", fontSize: 20, color: "#64748B", cursor: "pointer", padding: "4px 8px" }}
              >×</button>
            </div>
            <div style={{ overflowY: "auto", flex: 1, padding: 8 }}>
              {visibleNavItems.map(item => {
                const isHidden = hiddenGroups.has(item.label);
                const isSingle = !!item.path;
                return (
                  <label
                    key={item.label}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 12px", borderRadius: 4, cursor: "pointer",
                      background: isHidden ? "#FEF2F2" : "#fff",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLLabelElement).style.background = isHidden ? "#FEE2E2" : "#F1F5F9"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLLabelElement).style.background = isHidden ? "#FEF2F2" : "#fff"; }}
                  >
                    <input
                      type="checkbox"
                      checked={!isHidden}
                      onChange={() => toggleGroupHidden(item.label)}
                      style={{ width: 16, height: 16, cursor: "pointer", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: isHidden ? "#94A3B8" : "#0F172A" }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>
                        {isSingle ? "Single page" : `${(item.children || []).length} available sub-items`}
                        {isHidden && " · HIDDEN"}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{
              padding: "10px 16px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <button
                onClick={() => {
                  setHiddenGroups(new Set());
                  try { localStorage.removeItem(HIDDEN_KEY); } catch {}
                }}
                style={{
                  background: "transparent", border: "1px solid #CBD5E1", color: "#475569",
                  borderRadius: 4, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >Show All</button>
              <div style={{ fontSize: 11, color: "#64748B" }}>
                {visibleHiddenCount} of {visibleNavItems.length} hidden
              </div>
              <button
                onClick={() => setCustomizeOpen(false)}
                style={{
                  background: "#1D4ED8", border: "none", color: "#fff",
                  borderRadius: 4, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                }}
              >Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Scrollable nav area ──────────────────────────── */}
      <div className="erp-sidebar-scroll" style={{
        flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden",
        padding: "8px 0", position: "relative", zIndex: 1,
      }}>
        {visibleNavItems.filter(matchesSearch).filter(item => !hiddenGroups.has(item.label)).map((item, idx) => {
          const color = COLORS[item.label] || "#3B82F6";

          // ── Single link item ────────────────────────────
          if (item.path) {
            if (!canView(item.mod || "", item.path)) return null;
            const active    = isActive(item.path);
            const isAlerts  = item.path === "/alerts";
            const badgeCnt  = isAlerts ? alertCounts.total    : 0;
            const badgeCrit = isAlerts ? alertCounts.critical : 0;
            const showBadge = isAlerts && badgeCnt > 0;
            const badgeBg   = badgeCrit > 0 ? "#DC2626" : "#D97706";

            return (
              <div key={item.label} className="erp-nav-item" style={{ position: "relative" }}
                onMouseEnter={e => {
                  if (collapsed) setTooltip({ label: item.label, y: e.currentTarget.getBoundingClientRect().top + 12 });
                }}
                onMouseLeave={() => setTooltip(null)}
              >
                <Link to={item.path} style={{ textDecoration: "none", display: "block" }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    height: 38,
                    margin: "2px 8px",
                    borderRadius: 8,
                    padding: collapsed ? "0" : "0 12px",
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: 10,
                    background: active
                      ? `linear-gradient(90deg, ${color}33, ${color}11)`
                      : "transparent",
                    borderLeft: active && !collapsed ? `3px solid ${color}` : !collapsed ? "3px solid transparent" : "none",
                    cursor: "pointer",
                    transition: "background 0.18s, transform 0.18s, box-shadow 0.18s",
                    boxShadow: active ? `0 0 12px ${color}33, inset 0 0 0 1px ${color}22` : "none",
                    color: active ? "#F9FAFB" : "#9CA3AF",
                    position: "relative",
                  }}
                  onMouseEnter={e => {
                    if (!active) {
                      e.currentTarget.style.background = "rgba(99,102,241,0.1)";
                      e.currentTarget.style.transform = "translateX(2px)";
                    }
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = active
                      ? `linear-gradient(90deg, ${color}33, ${color}11)` : "transparent";
                    e.currentTarget.style.transform = "translateX(0)";
                  }}
                  >
                    <span style={{
                      color: active ? color : "#6B7280",
                      flexShrink: 0, display: "flex", alignItems: "center", position: "relative",
                      filter: active ? `drop-shadow(0 0 4px ${color}aa)` : "none",
                      transition: "filter 0.2s, color 0.2s",
                    }}>
                      {IC[item.icon || "dashboard"]}
                      {/* Badge when collapsed — overlay dot on icon */}
                      {showBadge && collapsed && (
                        <span style={{
                          position: "absolute", top: -4, right: -6,
                          background: badgeBg, color: "#fff",
                          borderRadius: 10, fontSize: 9, fontWeight: 700,
                          padding: "1px 5px", minWidth: 14, height: 14,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          border: "1.5px solid #111827",
                          boxShadow: "0 0 0 1px rgba(0,0,0,0.3)",
                        }}>{badgeCnt > 99 ? "99+" : badgeCnt}</span>
                      )}
                    </span>
                    {!collapsed && (
                      <>
                        <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, whiteSpace: "nowrap", flex: 1 }}>
                          {item.label}
                        </span>
                        {showBadge && (
                          <span style={{
                            background: badgeBg, color: "#fff",
                            borderRadius: 10, fontSize: 10, fontWeight: 700,
                            padding: "2px 7px", minWidth: 18,
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            animation: badgeCrit > 0 ? "pulseBadge 1.6s ease-in-out infinite" : "none",
                          }}>{badgeCnt > 99 ? "99+" : badgeCnt}</span>
                        )}
                      </>
                    )}
                  </div>
                </Link>
              </div>
            );
          }

          // ── Group with children ─────────────────────────
          const isOpen        = !!open[item.label];
          const hasActive     = item.children?.some(c => location.pathname.startsWith(c.path.split("?")[0])) ?? false;
          const visibleChildren = item.children?.filter(c => canView(c.mod, c.path)) ?? [];
          if (visibleChildren.length === 0) return null;

          return (
            <div key={item.label} className="erp-nav-item">
              {/* Group header row (icon + label) */}
              <div
                onClick={() => !collapsed && toggle(item.label)}
                onMouseEnter={e => {
                  if (!hasActive) {
                    e.currentTarget.style.background = "rgba(99,102,241,0.1)";
                    e.currentTarget.style.transform = "translateX(2px)";
                  }
                  if (collapsed) setTooltip({ label: item.label, y: e.currentTarget.getBoundingClientRect().top + 12 });
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = hasActive && !collapsed
                    ? `linear-gradient(90deg, ${color}2A, ${color}0D)` : "transparent";
                  e.currentTarget.style.transform = "translateX(0)";
                  setTooltip(null);
                }}
                style={{
                  display: "flex", alignItems: "center",
                  height: 38,
                  margin: "2px 8px",
                  borderRadius: 8,
                  padding: collapsed ? "0" : "0 12px",
                  justifyContent: collapsed ? "center" : "flex-start",
                  gap: 10,
                  background: hasActive && !collapsed
                    ? `linear-gradient(90deg, ${color}2A, ${color}0D)` : "transparent",
                  borderLeft: hasActive && !collapsed ? `3px solid ${color}` : !collapsed ? "3px solid transparent" : "none",
                  cursor: "pointer",
                  transition: "background 0.18s, transform 0.18s, box-shadow 0.18s",
                  color: hasActive ? "#F9FAFB" : "#9CA3AF",
                  userSelect: "none",
                  boxShadow: hasActive ? `0 0 10px ${color}25, inset 0 0 0 1px ${color}18` : "none",
                }}
              >
                <span style={{
                  color: hasActive ? color : "#6B7280",
                  flexShrink: 0, display: "flex", alignItems: "center",
                  filter: hasActive ? `drop-shadow(0 0 4px ${color}aa)` : "none",
                  transition: "filter 0.2s, color 0.2s",
                }}>
                  {IC[item.icon || "dashboard"]}
                </span>
                {!collapsed && (
                  <>
                    <span style={{ fontSize: 13, fontWeight: hasActive ? 600 : 500, flex: 1, whiteSpace: "nowrap" }}>
                      {item.label}
                    </span>
                    <span style={{
                      color: hasActive ? color : "#4B5563",
                      display: "flex", alignItems: "center",
                      transition: "transform 0.22s, color 0.2s",
                      transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                    }}>
                      {IC.chevronD}
                    </span>
                  </>
                )}
              </div>

              {/* Child items */}
              {!collapsed && isOpen && (
                <div>
                  {visibleChildren.map(child => {
                    const active = isActive(child.path);
                    return (
                      <Link key={child.path} to={child.path} style={{ textDecoration: "none", display: "block" }}>
                        <div
                          className="erp-nav-item"
                          style={{
                            display: "flex", alignItems: "center", gap: 8,
                            height: 32,
                            margin: "1px 8px 1px 24px",
                            borderRadius: 6,
                            padding: "0 12px",
                            background: active ? `linear-gradient(90deg, ${color}33, ${color}0D)` : "transparent",
                            borderLeft: active ? `2px solid ${color}` : "2px solid rgba(99,102,241,0.08)",
                            cursor: "pointer",
                            transition: "background 0.18s, transform 0.18s, box-shadow 0.18s",
                            color: active ? "#F9FAFB" : "#6B7280",
                            boxShadow: active ? `0 0 8px ${color}22` : "none",
                          }}
                          onMouseEnter={e => {
                            if (!active) {
                              e.currentTarget.style.background = "rgba(99,102,241,0.08)";
                              e.currentTarget.style.color = "#CBD5E1";
                              e.currentTarget.style.transform = "translateX(2px)";
                            }
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = active ? `linear-gradient(90deg, ${color}33, ${color}0D)` : "transparent";
                            e.currentTarget.style.color = active ? "#F9FAFB" : "#6B7280";
                            e.currentTarget.style.transform = "translateX(0)";
                          }}
                        >
                          <div style={{
                            width: 5, height: 5, borderRadius: "50%", flexShrink: 0,
                            background: active ? color : "#374151",
                            boxShadow: active ? `0 0 6px ${color}` : "none",
                            transition: "background 0.2s, box-shadow 0.2s",
                          }} />
                          <span style={{ fontSize: 12.5, fontWeight: active ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {child.label}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Version footer (always inside sidebar bounds) ── */}
      <div style={{
        flexShrink: 0,
        padding: collapsed ? "8px 4px" : "8px 12px",
        borderTop: "1px solid #1F2937",
        fontSize: collapsed ? 8 : 10,
        color: "#4B5563",
        textAlign: "center",
        letterSpacing: "0.3px",
        userSelect: "none",
        background: "#0B1220",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>
        {collapsed ? `v${branding.version || "2.0"}` : (branding.footer_text || `${branding.app_name} v${branding.version || "2.0"}`)}
      </div>

      {/* ── Tooltip (collapsed mode) ─────────────────────── */}
      {collapsed && tooltip && (
        <div style={{
          position: "fixed",
          left: 62, top: tooltip.y - 8,
          background: "#1F2937",
          color: "#F9FAFB",
          fontSize: 12, fontWeight: 600,
          padding: "5px 12px",
          borderRadius: 6,
          border: "1px solid #374151",
          pointerEvents: "none",
          zIndex: 9999,
          whiteSpace: "nowrap",
          boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
        }}>
          {tooltip.label}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
